import {
    useState, useRef,
    ChangeEvent, MouseEvent, ReactNode,
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminProfile {
    name: string;
    email: string;
    phone: string;
    role: string;
    bio: string;
    timezone: string;
    language: string;
    avatarInitials: string;
}

interface NotificationSettings {
    emailNewQuote: boolean;
    emailInvoicePaid: boolean;
    emailOverdueInvoice: boolean;
    emailNewMessage: boolean;
    emailWeeklySummary: boolean;
    browserPush: boolean;
    smsUrgentAlerts: boolean;
}

interface SecuritySettings {
    twoFAEnabled: boolean;
    sessionTimeout: string;
    loginNotifications: boolean;
    trustedDevices: TrustedDevice[];
    activeSessions: ActiveSession[];
}

interface TrustedDevice {
    id: string;
    name: string;
    browser: string;
    os: string;
    lastSeen: string;
    isCurrent: boolean;
}

interface ActiveSession {
    id: string;
    device: string;
    location: string;
    ip: string;
    startedAt: string;
    isCurrent: boolean;
}

interface ActivityLog {
    id: string;
    action: string;
    detail: string;
    ip: string;
    at: string;
    type: "auth" | "setting" | "content" | "security";
}

type SettingsSection =
    | "profile"
    | "password"
    | "security"
    | "notifications"
    | "appearance"
    | "api"
    | "activity"
    | "danger";

interface ToastState { visible: boolean; message: string; type: "success" | "error" | "warning" | "info"; }

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_PROFILE: AdminProfile = {
    name: "Admin User",
    email: "admin@auralglobal.com",
    phone: "+44 20 7946 0311",
    role: "Super Administrator",
    bio: "Managing the Aurel Global admin panel and overseeing all product, certification, and sales operations.",
    timezone: "Europe/London",
    language: "en-GB",
    avatarInitials: "AU",
};

const MOCK_NOTIFICATIONS: NotificationSettings = {
    emailNewQuote: true,
    emailInvoicePaid: true,
    emailOverdueInvoice: true,
    emailNewMessage: false,
    emailWeeklySummary: true,
    browserPush: false,
    smsUrgentAlerts: false,
};

const MOCK_DEVICES: TrustedDevice[] = [
    { id: "dev-1", name: "MacBook Pro 16", browser: "Chrome 123", os: "macOS 14.4", lastSeen: "Just now", isCurrent: true },
    { id: "dev-2", name: "iPhone 15 Pro", browser: "Safari 17", os: "iOS 17.4", lastSeen: "2 hours ago", isCurrent: false },
    { id: "dev-3", name: "Windows Workstation", browser: "Edge 122", os: "Windows 11", lastSeen: "3 days ago", isCurrent: false },
];

const MOCK_SESSIONS: ActiveSession[] = [
    { id: "sess-1", device: "Chrome / macOS", location: "London, UK", ip: "82.14.221.93", startedAt: "Today, 09:12", isCurrent: true },
    { id: "sess-2", device: "Safari / iOS", location: "London, UK", ip: "82.14.221.93", startedAt: "Today, 06:45", isCurrent: false },
    { id: "sess-3", device: "Edge / Windows", location: "Birmingham, UK", ip: "92.23.108.44", startedAt: "3 days ago", isCurrent: false },
];

const MOCK_ACTIVITY: ActivityLog[] = [
    { id: "a1", action: "Password changed", detail: "Password updated successfully", ip: "82.14.221.93", at: "Today, 10:23", type: "security" },
    { id: "a2", action: "Login successful", detail: "Chrome · macOS · London, UK", ip: "82.14.221.93", at: "Today, 09:12", type: "auth" },
    { id: "a3", action: "Product updated", detail: "Edited 'GNSS Receiver GNX-Pro'", ip: "82.14.221.93", at: "Today, 09:31", type: "content" },
    { id: "a4", action: "Invoice status changed", detail: "INV-2025-0003 → Sent", ip: "82.14.221.93", at: "Yesterday, 16:05", type: "setting" },
    { id: "a5", action: "Certification published", detail: "Published 'ISO 9001:2015'", ip: "82.14.221.93", at: "Yesterday, 14:22", type: "content" },
    { id: "a6", action: "Login successful", detail: "Safari · iOS · London, UK", ip: "82.14.221.93", at: "Yesterday, 08:01", type: "auth" },
    { id: "a7", action: "Two-factor auth enabled", detail: "TOTP authenticator configured", ip: "82.14.221.93", at: "3 days ago, 11:44", type: "security" },
    { id: "a8", action: "Quote status changed", detail: "QR-2025-1002 → Reviewing", ip: "92.23.108.44", at: "3 days ago, 09:18", type: "setting" },
    { id: "a9", action: "File uploaded", detail: "gnss-receiver-photo.png (3.1 MB)", ip: "82.14.221.93", at: "4 days ago, 15:02", type: "content" },
    { id: "a10", action: "Login failed", detail: "Incorrect password · 2 attempts", ip: "77.91.12.44", at: "5 days ago, 22:14", type: "security" },
    { id: "a11", action: "Notification settings saved", detail: "Email preferences updated", ip: "82.14.221.93", at: "6 days ago, 10:55", type: "setting" },
    { id: "a12", action: "Product created", detail: "Created 'Drone Survey Bundle'", ip: "82.14.221.93", at: "7 days ago, 14:30", type: "content" },
];

const MOCK_API_KEYS = [
    { id: "key-1", name: "Production API", key: "ag_live_sk_••••••••••••••••4f2a", created: "Jan 12, 2025", lastUsed: "Today", scopes: ["read", "write"] },
    { id: "key-2", name: "Analytics Read", key: "ag_live_sk_••••••••••••••••91bc", created: "Feb 3, 2025", lastUsed: "Yesterday", scopes: ["read"] },
    { id: "key-3", name: "Webhook Service", key: "ag_live_sk_••••••••••••••••773d", created: "Mar 5, 2025", lastUsed: "3 days ago", scopes: ["webhooks"] },
];

const TIMEZONES = ["Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Los_Angeles", "Asia/Dubai", "Asia/Kolkata", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland"];
const LANGUAGES = [{ code: "en-GB", label: "English (UK)" }, { code: "en-US", label: "English (US)" }, { code: "fr-FR", label: "Français" }, { code: "de-DE", label: "Deutsch" }, { code: "ar-AE", label: "العربية" }, { code: "ja-JP", label: "日本語" }];
const SESSION_TIMEOUTS = [{ value: "30m", label: "30 minutes" }, { value: "1h", label: "1 hour" }, { value: "4h", label: "4 hours" }, { value: "8h", label: "8 hours" }, { value: "24h", label: "24 hours" }, { value: "7d", label: "7 days" }];

// ─── Password strength ────────────────────────────────────────────────────────

function measureStrength(pw: string): { score: number; label: string; color: string } {
    if (!pw) return { score: 0, label: "", color: "transparent" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { score, label: "Very weak", color: "#e05252" };
    if (score === 2) return { score, label: "Weak", color: "#e05252" };
    if (score === 3) return { score, label: "Fair", color: "#f5a623" };
    if (score === 4) return { score, label: "Strong", color: "#6ddc6d" };
    return { score, label: "Very strong", color: "#6ddc6d" };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid() { return Math.random().toString(36).slice(2, 10); }

// ─── Base input styles ────────────────────────────────────────────────────────

const inputBase: React.CSSProperties = {
    width: "100%", background: "var(--input-bg)", border: "1px solid var(--border)",
    borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "var(--text)",
    outline: "none", fontFamily: "'DM Sans', sans-serif",
    transition: "border-color 0.15s, box-shadow 0.15s", boxSizing: "border-box",
};

// ─── Shared atoms ─────────────────────────────────────────────────────────────

function SectionCard({ title, subtitle, children, danger }: { title: string; subtitle?: string; children: ReactNode; danger?: boolean }) {
    return (
        <div style={{
            background: danger ? "rgba(224,82,82,0.04)" : "var(--card)",
            border: `1px solid ${danger ? "rgba(224,82,82,0.2)" : "var(--border)"}`,
            borderRadius: 14, overflow: "hidden",
        }}>
            <div style={{
                padding: "16px 20px", borderBottom: `1px solid ${danger ? "rgba(224,82,82,0.15)" : "var(--border)"}`,
                background: danger ? "rgba(224,82,82,0.03)" : "var(--section-header)",
            }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: danger ? "#e05252" : "var(--text)" }}>{title}</div>
                {subtitle && <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 3 }}>{subtitle}</div>}
            </div>
            <div style={{ padding: 20 }}>{children}</div>
        </div>
    );
}

function Field({ label, hint, children, required }: { label: string; hint?: string; children: ReactNode; required?: boolean }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                    {label}{required && <span style={{ color: "var(--or)", marginLeft: 3 }}>*</span>}
                </label>
                {hint && <span style={{ fontSize: 11, color: "var(--text3)" }}>{hint}</span>}
            </div>
            {children}
        </div>
    );
}

function SettingRow({ label, subtitle, children }: { label: string; subtitle?: string; children: ReactNode }) {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{label}</div>
                {subtitle && <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2, lineHeight: 1.5 }}>{subtitle}</div>}
            </div>
            <div style={{ flexShrink: 0 }}>{children}</div>
        </div>
    );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
    return (
        <div onClick={onChange} style={{
            width: 38, height: 22, borderRadius: 11,
            background: checked ? "var(--or)" : "var(--border-strong, rgba(255,255,255,0.18))",
            position: "relative", cursor: "pointer", flexShrink: 0,
            transition: "background 0.25s",
            border: `1px solid ${checked ? "rgba(191,61,12,0.4)" : "rgba(255,255,255,0.08)"}`,
        }}>
            <div style={{
                position: "absolute", top: 2, left: checked ? 16 : 2,
                width: 16, height: 16, borderRadius: "50%", background: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.3)", transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
            }} />
        </div>
    );
}

function StyledInput({ value, onChange, placeholder, type = "text", disabled }: { value: string; onChange?: (e: ChangeEvent<HTMLInputElement>) => void; placeholder?: string; type?: string; disabled?: boolean }) {
    const [focused, setFocused] = useState(false);
    return (
        <input value={value} onChange={onChange} placeholder={placeholder} type={type} disabled={disabled}
            style={{
                ...inputBase,
                borderColor: focused ? "var(--or)" : "var(--border)",
                boxShadow: focused ? "0 0 0 3px rgba(191,61,12,0.10)" : "none",
                opacity: disabled ? 0.5 : 1,
            }}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        />
    );
}

function StyledTextarea({ value, onChange, placeholder, rows = 3 }: { value: string; onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void; placeholder?: string; rows?: number }) {
    const [focused, setFocused] = useState(false);
    return (
        <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
            style={{
                ...inputBase, resize: "vertical", minHeight: rows * 24, lineHeight: 1.6,
                borderColor: focused ? "var(--or)" : "var(--border)",
                boxShadow: focused ? "0 0 0 3px rgba(191,61,12,0.10)" : "none",
            }}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        />
    );
}

function StyledSelect({ value, onChange, children }: { value: string; onChange: (e: ChangeEvent<HTMLSelectElement>) => void; children: ReactNode }) {
    return (
        <select value={value} onChange={onChange} style={{
            ...inputBase, cursor: "pointer", appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236a6a65' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
            paddingRight: 36,
        }}>
            {children}
        </select>
    );
}

function SaveBtn({ onClick, saving, label = "Save changes" }: { onClick: () => void; saving: boolean; label?: string }) {
    return (
        <button onClick={onClick} disabled={saving} style={{
            padding: "10px 22px", borderRadius: 9, fontSize: 14, fontWeight: 500, cursor: "pointer",
            border: "1px solid rgba(191,61,12,0.35)",
            background: saving ? "rgba(191,61,12,0.4)" : "linear-gradient(135deg,#BF3D0C,#E8593C)",
            color: "#fff", fontFamily: "'DM Sans',sans-serif",
            boxShadow: "0 2px 12px rgba(191,61,12,0.25)", transition: "opacity 0.15s, transform 0.15s",
            display: "flex", alignItems: "center", gap: 6,
        }}>{saving ? (<><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite", display: "inline-block" }} />Saving…</>) : label}</button>
    );
}

function DangerBtn({ onClick, label }: { onClick: () => void; label: string }) {
    const [hov, setHov] = useState(false);
    return (
        <button onClick={onClick}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{
                padding: "9px 18px", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer",
                border: "1px solid rgba(224,82,82,0.35)",
                background: hov ? "rgba(224,82,82,0.15)" : "rgba(224,82,82,0.08)",
                color: "#e05252", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
            }}>{label}</button>
    );
}

// ─── Activity log entry ───────────────────────────────────────────────────────

const ACT_TYPE_CFG = {
    auth: { icon: "🔑", color: "#8ab4f8" },
    setting: { icon: "⚙️", color: "var(--or)" },
    content: { icon: "📝", color: "#4ec9a6" },
    security: { icon: "🛡", color: "#c678f5" },
};

function ActivityEntry({ entry }: { entry: ActivityLog }) {
    const cfg = ACT_TYPE_CFG[entry.type];
    return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
            <div style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0, fontSize: 14,
                background: `${cfg.color}18`, border: `1px solid ${cfg.color}44`,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>{cfg.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 2 }}>{entry.action}</div>
                <div style={{ fontSize: 12, color: "var(--text3)" }}>{entry.detail}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>{entry.at}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "monospace", marginTop: 2 }}>{entry.ip}</div>
            </div>
        </div>
    );
}

// ─── Confirm modal ────────────────────────────────────────────────────────────

function ConfirmModal({ open, title, message, confirmLabel, onConfirm, onCancel, danger = true }: {
    open: boolean; title: string; message: string; confirmLabel: string;
    onConfirm: () => void; onCancel: () => void; danger?: boolean;
}) {
    if (!open) return null;
    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onCancel}>
            <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{ background: "#1a1917", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 28, width: 400, maxWidth: "90vw", animation: "modalIn 0.2s ease" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: danger ? "rgba(224,82,82,0.12)" : "var(--or-dim)", border: `1px solid ${danger ? "rgba(224,82,82,0.25)" : "rgba(191,61,12,0.3)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 16 }}>{danger ? "⚠️" : "✓"}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>{title}</div>
                <div style={{ fontSize: 14, color: "var(--text3)", lineHeight: 1.7, marginBottom: 24 }}>{message}</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={onCancel} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Cancel</button>
                    <button onClick={onConfirm} style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${danger ? "rgba(224,82,82,0.35)" : "rgba(191,61,12,0.35)"}`, background: danger ? "rgba(224,82,82,0.12)" : "var(--or-dim)", color: danger ? "#e05252" : "var(--or)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

// ─── Password input with show/hide ────────────────────────────────────────────

function PasswordInput({ value, onChange, placeholder }: { value: string; onChange: (e: ChangeEvent<HTMLInputElement>) => void; placeholder?: string }) {
    const [show, setShow] = useState(false);
    const [focused, setFocused] = useState(false);
    return (
        <div style={{ position: "relative" }}>
            <input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder}
                style={{ ...inputBase, paddingRight: 40, borderColor: focused ? "var(--or)" : "var(--border)", boxShadow: focused ? "0 0 0 3px rgba(191,61,12,0.10)" : "none" }}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
            <button onClick={() => setShow(!show)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text3)", padding: 4, display: "flex" }}>
                {show ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                )}
            </button>
        </div>
    );
}

// ─── Nav item ─────────────────────────────────────────────────────────────────

function NavItem({ id, label, icon, active, onClick, badge }: { id: SettingsSection; label: string; icon: string; active: boolean; onClick: () => void; badge?: number }) {
    return (
        <button onClick={onClick} style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%",
            padding: "10px 12px", borderRadius: 9, marginBottom: 2, cursor: "pointer",
            background: active ? "var(--or-dim)" : "transparent",
            border: `1px solid ${active ? "rgba(191,61,12,0.2)" : "transparent"}`,
            color: active ? "var(--or)" : "var(--text2)", fontFamily: "'DM Sans',sans-serif",
            textAlign: "left", transition: "all 0.15s",
        }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
            <span style={{ fontSize: 14, fontWeight: active ? 500 : 400, flex: 1 }}>{label}</span>
            {badge !== undefined && badge > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--or)", background: "var(--or-dim)", border: "1px solid rgba(191,61,12,0.2)", borderRadius: 100, padding: "1px 6px" }}>{badge}</span>
            )}
        </button>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminControls() {
    const [activeSection, setActiveSection] = useState<SettingsSection>("profile");

    // Profile
    const [profile, setProfile] = useState<AdminProfile>(MOCK_PROFILE);
    const [profileSaving, setProfileSaving] = useState(false);

    // Password
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [pwError, setPwError] = useState("");
    const [pwSaving, setPwSaving] = useState(false);

    // Security
    const [security, setSecurity] = useState<SecuritySettings>({
        twoFAEnabled: true,
        sessionTimeout: "8h",
        loginNotifications: true,
        trustedDevices: MOCK_DEVICES,
        activeSessions: MOCK_SESSIONS,
    });
    const [secSaving, setSecSaving] = useState(false);

    // Notifications
    const [notifs, setNotifs] = useState<NotificationSettings>(MOCK_NOTIFICATIONS);
    const [notifSaving, setNotifSaving] = useState(false);

    // API keys
    const [apiKeys, setApiKeys] = useState(MOCK_API_KEYS);
    const [newKeyName, setNewKeyName] = useState("");
    const [revealedKey, setRevealedKey] = useState<string | null>(null);

    // Confirm modal
    const [confirm, setConfirm] = useState({ open: false, title: "", message: "", label: "", action: () => { }, danger: true });
    const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

    // Toast
    const [toast, setToast] = useState<ToastState>({ visible: false, message: "", type: "success" });
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showToast = (message: string, type: ToastState["type"] = "success") => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ visible: true, message, type });
        toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 3200);
    };

    const simulateSave = async (setter: (v: boolean) => void, msg: string) => {
        setter(true);
        await new Promise((r) => setTimeout(r, 900));
        setter(false);
        showToast(msg);
    };

    // ── Password logic ────────────────────────────────────────────────────────

    const handlePasswordSave = async () => {
        setPwError("");
        if (!currentPw) { setPwError("Current password is required."); return; }
        if (newPw.length < 8) { setPwError("New password must be at least 8 characters."); return; }
        if (newPw !== confirmPw) { setPwError("Passwords do not match."); return; }
        const strength = measureStrength(newPw);
        if (strength.score < 3) { setPwError("Password is too weak. Add uppercase letters, numbers, and symbols."); return; }
        setPwSaving(true);
        await new Promise((r) => setTimeout(r, 1000));
        setPwSaving(false);
        setCurrentPw(""); setNewPw(""); setConfirmPw(""); setPwError("");
        showToast("Password changed successfully");
    };

    const pwStrength = measureStrength(newPw);

    // ── Sections config ───────────────────────────────────────────────────────

    const NAV: { id: SettingsSection; label: string; icon: string; badge?: number }[] = [
        { id: "profile", label: "Profile", icon: "👤" },
        { id: "password", label: "Password", icon: "🔑" },
        { id: "security", label: "Security", icon: "🛡", badge: security.twoFAEnabled ? undefined : 1 },
        { id: "notifications", label: "Notifications", icon: "🔔" },
        { id: "appearance", label: "Appearance", icon: "🎨" },
        { id: "api", label: "API Keys", icon: "⚡" },
        { id: "activity", label: "Activity Log", icon: "📋" },
        { id: "danger", label: "Danger Zone", icon: "⚠️" },
    ];

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --border-strong:rgba(255,255,255,0.18);
        //   --card:rgba(255,255,255,0.025); --input-bg:rgba(255,255,255,0.035);
        //   --section-header:rgba(255,255,255,0.02); --surface:#111110;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder, textarea::placeholder { color:var(--text3); }
        input, textarea, select { color:var(--text); }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:var(--border); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes modalIn     { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        @keyframes spin        { to{transform:rotate(360deg)} }
        .st-layout { display:grid; grid-template-columns:220px 1fr; gap:24px; align-items:start; }
        .st-nav    { position:sticky; top:24px; }
        @media(max-width:900px){ .st-layout{grid-template-columns:1fr;} .st-nav{position:static;} }
        select option { background:#1a1917; color:#e8e6de; }
      `}</style>

            {/* Toast */}
            {toast.visible && (
                <div style={{
                    position: "fixed", top: 20, right: 20, zIndex: 700,
                    background: toast.type === "success" ? "#1a2e1a" : toast.type === "warning" ? "#2e2a1a" : "#2e1a1a",
                    border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : toast.type === "warning" ? "rgba(245,166,35,0.25)" : "rgba(224,82,82,0.25)"}`,
                    borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
                    animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                }}>
                    <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : toast.type === "warning" ? "#f5d68a" : "#e08b8b" }}>
                        {toast.type === "success" ? "✓" : toast.type === "warning" ? "⚠" : "✕"}&nbsp;&nbsp;{toast.message}
                    </span>
                </div >
            )
            }

            <ConfirmModal confirmLabel="Confirm" {...confirm} onConfirm={() => { confirm.action(); closeConfirm(); }} onCancel={closeConfirm} />

            <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

                    {/* Header */}
                    <div style={{ marginBottom: 28 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>System</div>
                        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>Settings</h1>
                        <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>Manage your account, security, and dashboard preferences.</p>
                    </div>

                    <div className="st-layout">

                        {/* ── Left nav ── */}
                        <nav className="st-nav" style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: "12px", display: "flex", flexDirection: "column", gap: 0 }}>
                            {/* Avatar */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 10px 14px", borderBottom: "1px solid var(--border)", marginBottom: 8 }}>
                                <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,var(--or),var(--or2))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                                    {profile.avatarInitials}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.name}</div>
                                    <div style={{ fontSize: 11, color: "var(--text3)" }}>{profile.role}</div>
                                </div>
                            </div>
                            {NAV.map((item) => (
                                <NavItem key={item.id} {...item} active={activeSection === item.id} onClick={() => setActiveSection(item.id)} />
                            ))}
                        </nav>

                        {/* ── Right content ── */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>

                            {/* ─────────────────────── PROFILE ─────────────────────── */}
                            {activeSection === "profile" && (
                                <>
                                    <SectionCard title="Profile Information" subtitle="Your public-facing name, email, and contact details">
                                        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid var(--border)" }}>
                                            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,var(--or),var(--or2))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                                                {profile.avatarInitials}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>{profile.name}</div>
                                                <div style={{ fontSize: 13, color: "var(--text3)", marginBottom: 8 }}>Avatar is generated from your initials.</div>
                                                <button onClick={() => showToast("Avatar upload coming soon", "warning")} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Change avatar</button>
                                            </div>
                                        </div>

                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                                            <Field label="Full name" required>
                                                <StyledInput value={profile.name} onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="Your full name" />
                                            </Field>
                                            <Field label="Email address" required>
                                                <StyledInput type="email" value={profile.email} onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))} placeholder="admin@company.com" />
                                            </Field>
                                            <Field label="Phone number">
                                                <StyledInput type="tel" value={profile.phone} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+44 20 7946 0000" />
                                            </Field>
                                            <Field label="Role" hint="Read-only">
                                                <StyledInput value={profile.role} disabled />
                                            </Field>
                                        </div>

                                        <div style={{ marginBottom: 16 }}>
                                            <Field label="Bio" hint="Shown in admin audit logs">
                                                <StyledTextarea value={profile.bio} onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))} placeholder="Brief description of your role…" rows={3} />
                                            </Field>
                                        </div>

                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                                            <Field label="Timezone">
                                                <StyledSelect value={profile.timezone} onChange={(e) => setProfile(p => ({ ...p, timezone: e.target.value }))}>
                                                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                                                </StyledSelect>
                                            </Field>
                                            <Field label="Language">
                                                <StyledSelect value={profile.language} onChange={(e) => setProfile(p => ({ ...p, language: e.target.value }))}>
                                                    {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                                                </StyledSelect>
                                            </Field>
                                        </div>

                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <SaveBtn onClick={() => simulateSave(setProfileSaving, "Profile updated successfully")} saving={profileSaving} />
                                        </div>
                                    </SectionCard>
                                </>
                            )}

                            {/* ─────────────────────── PASSWORD ─────────────────────── */}
                            {activeSection === "password" && (
                                <SectionCard title="Change Password" subtitle="Use a strong password of at least 12 characters with mixed case, numbers and symbols">
                                    <div style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: 16 }}>
                                        <Field label="Current password" required>
                                            <PasswordInput value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} placeholder="Enter current password" />
                                        </Field>
                                        <Field label="New password" required hint={newPw ? `${newPw.length} chars` : undefined}>
                                            <PasswordInput value={newPw} onChange={(e) => { setNewPw(e.target.value); setPwError(""); }} placeholder="At least 12 characters recommended" />
                                            {newPw && (
                                                <div style={{ marginTop: 6 }}>
                                                    <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
                                                        {[1, 2, 3, 4, 5].map((i) => (
                                                            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= pwStrength.score ? pwStrength.color : "rgba(255,255,255,0.08)", transition: "background 0.3s" }} />
                                                        ))}
                                                    </div>
                                                    <span style={{ fontSize: 11, color: pwStrength.color, fontWeight: 600 }}>{pwStrength.label}</span>
                                                </div>
                                            )}
                                        </Field>
                                        <Field label="Confirm new password" required>
                                            <PasswordInput value={confirmPw} onChange={(e) => { setConfirmPw(e.target.value); setPwError(""); }} placeholder="Repeat your new password" />
                                            {confirmPw && confirmPw !== newPw && (
                                                <span style={{ fontSize: 12, color: "#e05252" }}>Passwords do not match</span>
                                            )}
                                            {confirmPw && confirmPw === newPw && (
                                                <span style={{ fontSize: 12, color: "#6ddc6d" }}>✓ Passwords match</span>
                                            )}
                                        </Field>

                                        {pwError && (
                                            <div style={{ background: "rgba(224,82,82,0.08)", border: "1px solid rgba(224,82,82,0.2)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#e05252" }}>
                                                {pwError}
                                            </div>
                                        )}

                                        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 8 }}>Password requirements</div>
                                            {[
                                                { label: "At least 8 characters", pass: newPw.length >= 8 },
                                                { label: "At least one uppercase letter (A–Z)", pass: /[A-Z]/.test(newPw) },
                                                { label: "At least one number (0–9)", pass: /[0-9]/.test(newPw) },
                                                { label: "At least one symbol (!@#$%^&*…)", pass: /[^A-Za-z0-9]/.test(newPw) },
                                            ].map((r) => (
                                                <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                                                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: r.pass && newPw ? "rgba(109,220,109,0.12)" : "rgba(255,255,255,0.05)", border: `1px solid ${r.pass && newPw ? "rgba(109,220,109,0.3)" : "var(--border)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: r.pass && newPw ? "#6ddc6d" : "var(--text3)", transition: "all 0.2s" }}>
                                                        {r.pass && newPw ? "✓" : "·"}
                                                    </div>
                                                    <span style={{ fontSize: 12, color: r.pass && newPw ? "var(--text2)" : "var(--text3)", transition: "color 0.2s" }}>{r.label}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4 }}>
                                            <SaveBtn onClick={handlePasswordSave} saving={pwSaving} label="Change password" />
                                        </div>
                                    </div>
                                </SectionCard>
                            )}

                            {/* ─────────────────────── SECURITY ─────────────────────── */}
                            {activeSection === "security" && (
                                <>
                                    <SectionCard title="Security Settings" subtitle="Manage two-factor authentication, session timeout and login alerts">
                                        <SettingRow label="Two-factor authentication" subtitle={security.twoFAEnabled ? "Active — TOTP authenticator app is configured." : "Disabled — your account is less secure without 2FA."}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                {!security.twoFAEnabled && (
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: "#f5a623", background: "rgba(245,166,35,0.10)", border: "1px solid rgba(245,166,35,0.28)", borderRadius: 4, padding: "2px 7px" }}>RECOMMENDED</span>
                                                )}
                                                <Toggle checked={security.twoFAEnabled} onChange={() => {
                                                    if (security.twoFAEnabled) {
                                                        setConfirm({ open: true, title: "Disable two-factor authentication?", message: "Removing 2FA significantly reduces your account security. Are you sure you want to continue?", label: "Disable 2FA", action: () => { setSecurity(s => ({ ...s, twoFAEnabled: false })); showToast("2FA disabled", "warning"); }, danger: true });
                                                    } else {
                                                        setSecurity(s => ({ ...s, twoFAEnabled: true })); showToast("2FA enabled successfully");
                                                    }
                                                }} />
                                            </div>
                                        </SettingRow>
                                        <SettingRow label="Login notifications" subtitle="Receive an email when a new device logs into your account">
                                            <Toggle checked={security.loginNotifications} onChange={() => setSecurity(s => ({ ...s, loginNotifications: !s.loginNotifications }))} />
                                        </SettingRow>
                                        <div style={{ paddingTop: 14 }}>
                                            <Field label="Session timeout" hint="Automatically sign out after inactivity">
                                                <div style={{ maxWidth: 220 }}>
                                                    <StyledSelect value={security.sessionTimeout} onChange={(e) => setSecurity(s => ({ ...s, sessionTimeout: e.target.value }))}>
                                                        {SESSION_TIMEOUTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                    </StyledSelect>
                                                </div>
                                            </Field>
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                                            <SaveBtn onClick={() => simulateSave(setSecSaving, "Security settings saved")} saving={secSaving} />
                                        </div>
                                    </SectionCard>

                                    <SectionCard title="Trusted Devices" subtitle="Devices that have been verified on this account">
                                        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                            {security.trustedDevices.map((dev, i) => (
                                                <div key={dev.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < security.trustedDevices.length - 1 ? "1px solid var(--border)" : "none" }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                                                        {dev.name.includes("iPhone") || dev.name.includes("iOS") ? "📱" : dev.name.includes("Windows") ? "🖥" : "💻"}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
                                                            <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{dev.name}</span>
                                                            {dev.isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: "#6ddc6d", background: "rgba(109,220,109,0.10)", border: "1px solid rgba(109,220,109,0.25)", borderRadius: 4, padding: "1px 5px" }}>CURRENT</span>}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "var(--text3)" }}>{dev.browser} · {dev.os} · {dev.lastSeen}</div>
                                                    </div>
                                                    {!dev.isCurrent && (
                                                        <button onClick={() => setConfirm({ open: true, title: "Remove trusted device?", message: `This will sign out "${dev.name}" and remove it from your trusted devices.`, label: "Remove device", action: () => { setSecurity(s => ({ ...s, trustedDevices: s.trustedDevices.filter(d => d.id !== dev.id) })); showToast("Device removed"); }, danger: true })} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 7, border: "1px solid rgba(224,82,82,0.3)", background: "transparent", color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Remove</button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </SectionCard>

                                    <SectionCard title="Active Sessions" subtitle="All locations where you are currently logged in">
                                        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                            {security.activeSessions.map((sess, i) => (
                                                <div key={sess.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < security.activeSessions.length - 1 ? "1px solid var(--border)" : "none" }}>
                                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: sess.isCurrent ? "#6ddc6d" : "var(--text3)", flexShrink: 0, boxShadow: sess.isCurrent ? "0 0 8px #6ddc6d44" : "none" }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
                                                            <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{sess.device}</span>
                                                            {sess.isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: "#6ddc6d", background: "rgba(109,220,109,0.10)", border: "1px solid rgba(109,220,109,0.25)", borderRadius: 4, padding: "1px 5px" }}>THIS SESSION</span>}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "var(--text3)" }}>{sess.location} · {sess.ip} · Started {sess.startedAt}</div>
                                                    </div>
                                                    {!sess.isCurrent && (
                                                        <button onClick={() => setConfirm({ open: true, title: "Terminate session?", message: "This will immediately sign out the device at this location.", label: "Terminate", action: () => { setSecurity(s => ({ ...s, activeSessions: s.activeSessions.filter(se => se.id !== sess.id) })); showToast("Session terminated"); }, danger: true })} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 7, border: "1px solid rgba(224,82,82,0.3)", background: "transparent", color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Terminate</button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 16 }}>
                                            <button onClick={() => setConfirm({ open: true, title: "Sign out all other sessions?", message: "All sessions except your current one will be immediately terminated.", label: "Sign out all others", action: () => { setSecurity(s => ({ ...s, activeSessions: s.activeSessions.filter(se => se.isCurrent) })); showToast("All other sessions terminated"); }, danger: true })} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(224,82,82,0.3)", background: "transparent", color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                                                Sign out all other sessions
                                            </button>
                                        </div>
                                    </SectionCard>
                                </>
                            )}

                            {/* ─────────────────────── NOTIFICATIONS ─────────────────────── */}
                            {activeSection === "notifications" && (
                                <SectionCard title="Notification Preferences" subtitle="Choose what events you want to be notified about and how">
                                    <div style={{ marginBottom: 4 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 0, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>Email notifications</div>
                                        {([
                                            { key: "emailNewQuote" as keyof NotificationSettings, label: "New quote request", sub: "When a customer submits a new sample or quote request" },
                                            { key: "emailInvoicePaid" as keyof NotificationSettings, label: "Invoice paid", sub: "When a customer completes a payment" },
                                            { key: "emailOverdueInvoice" as keyof NotificationSettings, label: "Overdue invoice", sub: "When an invoice passes its due date unpaid" },
                                            { key: "emailNewMessage" as keyof NotificationSettings, label: "New contact message", sub: "When a visitor submits the contact form" },
                                            { key: "emailWeeklySummary" as keyof NotificationSettings, label: "Weekly summary", sub: "A digest of key activity every Monday morning" },
                                        ]).map((item) => (
                                            <SettingRow key={item.key} label={item.label} subtitle={item.sub}>
                                                <Toggle checked={!!notifs[item.key]} onChange={() => setNotifs(n => ({ ...n, [item.key]: !n[item.key] }))} />
                                            </SettingRow>
                                        ))}
                                    </div>

                                    <div style={{ marginTop: 16 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>Other channels</div>
                                        {([
                                            { key: "browserPush" as keyof NotificationSettings, label: "Browser push notifications", sub: "Receive desktop alerts when the dashboard is open" },
                                            { key: "smsUrgentAlerts" as keyof NotificationSettings, label: "SMS for urgent alerts", sub: "Text message for overdue invoices and security events" },
                                        ]).map((item) => (
                                            <SettingRow key={item.key} label={item.label} subtitle={item.sub}>
                                                <Toggle checked={!!notifs[item.key]} onChange={() => setNotifs(n => ({ ...n, [item.key]: !n[item.key] }))} />
                                            </SettingRow>
                                        ))}
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                                        <SaveBtn onClick={() => simulateSave(setNotifSaving, "Notification preferences saved")} saving={notifSaving} />
                                    </div>
                                </SectionCard>
                            )}

                            {/* ─────────────────────── APPEARANCE ─────────────────────── */}
                            {activeSection === "appearance" && (
                                <SectionCard title="Appearance" subtitle="Customise the look and feel of your admin dashboard">
                                    <div style={{ padding: "20px 0", textAlign: "center", color: "var(--text3)" }}>
                                        <div style={{ fontSize: 32, marginBottom: 12 }}>🎨</div>
                                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>Theme settings are managed by the Theme component</div>
                                        <div style={{ fontSize: 13, marginBottom: 20 }}>Use the Theme Toggle in the sidebar footer or top bar to switch between Dark, Light, and System modes.</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxWidth: 360, margin: "0 auto" }}>
                                            {[
                                                { label: "Dark", desc: "Current default", bg: "#111110", text: "rgba(255,255,255,0.7)", active: true },
                                                { label: "Light", desc: "High contrast", bg: "#f5f4f0", text: "rgba(0,0,0,0.5)", active: false },
                                            ].map((t) => (
                                                <div key={t.label} style={{ borderRadius: 10, overflow: "hidden", border: `2px solid ${t.active ? "var(--or)" : "var(--border)"}`, cursor: "pointer" }}>
                                                    <div style={{ background: t.bg, height: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <div style={{ width: 40, height: 6, borderRadius: 3, background: t.active ? "#BF3D0C" : t.text, opacity: 0.5 }} />
                                                    </div>
                                                    <div style={{ background: t.active ? "#161614" : "#ffffff", padding: "6px 10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                        <span style={{ fontSize: 12, color: t.active ? "var(--text)" : "rgba(0,0,0,0.6)" }}>{t.label}</span>
                                                        {t.active && <span style={{ fontSize: 10, color: "var(--or)", fontWeight: 700 }}>ACTIVE</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </SectionCard>
                            )}

                            {/* ─────────────────────── API KEYS ─────────────────────── */}
                            {activeSection === "api" && (
                                <>
                                    <SectionCard title="API Keys" subtitle="Manage access tokens for integrations and external services">
                                        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                            {apiKeys.map((k, i) => (
                                                <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: i < apiKeys.length - 1 ? "1px solid var(--border)" : "none" }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(191,61,12,0.1)", border: "1px solid rgba(191,61,12,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>⚡</div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                                                            <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{k.name}</span>
                                                            {k.scopes.map((s) => (
                                                                <span key={s} style={{ fontSize: 10, fontWeight: 600, color: "var(--or)", background: "var(--or-dim)", border: "1px solid rgba(191,61,12,0.2)", borderRadius: 4, padding: "1px 5px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{s}</span>
                                                            ))}
                                                        </div>
                                                        <div style={{ fontSize: 12, fontFamily: "monospace", color: "var(--text3)", marginBottom: 2 }}>
                                                            {revealedKey === k.id ? k.key.replace(/•+/g, "LIVE_KEY_SHOWN") : k.key}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: "var(--text3)" }}>Created {k.created} · Last used {k.lastUsed}</div>
                                                    </div>
                                                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                                        <button onClick={() => setRevealedKey(revealedKey === k.id ? null : k.id)} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text3)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>{revealedKey === k.id ? "Hide" : "Reveal"}</button>
                                                        <button onClick={() => { navigator.clipboard?.writeText("ag_live_sk_example"); showToast("API key copied", "info"); }} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text3)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Copy</button>
                                                        <button onClick={() => setConfirm({ open: true, title: `Revoke "${k.name}"?`, message: "This API key will immediately stop working. This cannot be undone.", label: "Revoke key", action: () => { setApiKeys(keys => keys.filter(key => key.id !== k.id)); showToast(`"${k.name}" revoked`, "warning"); }, danger: true })} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(224,82,82,0.3)", background: "transparent", color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Revoke</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)", display: "flex", gap: 10, alignItems: "center" }}>
                                            <StyledInput value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="New key name (e.g. Mobile App)" />
                                            <button onClick={() => {
                                                if (!newKeyName.trim()) { showToast("Enter a key name first", "warning"); return; }
                                                setApiKeys(k => [...k, { id: uid(), name: newKeyName, key: "ag_live_sk_••••••••••••••••" + uid().slice(0, 4), created: "Just now", lastUsed: "Never", scopes: ["read"] }]);
                                                setNewKeyName(""); showToast(`API key "${newKeyName}" created`);
                                            }} style={{ padding: "10px 18px", borderRadius: 8, border: "1px solid rgba(191,61,12,0.35)", background: "var(--or-dim)", color: "var(--or)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap" }}>
                                                + Generate key
                                            </button>
                                        </div>
                                    </SectionCard>

                                    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px" }}>
                                        <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.8 }}>
                                            ⚠️ <strong style={{ color: "var(--text2)" }}>Keep your API keys secure.</strong> Never share them in public repositories, client-side code, or insecure channels. Rotate keys periodically and revoke any that are no longer in use.
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ─────────────────────── ACTIVITY LOG ─────────────────────── */}
                            {activeSection === "activity" && (
                                <SectionCard title="Activity Log" subtitle="A record of all actions taken on your account in the last 30 days">
                                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                        {MOCK_ACTIVITY.map((entry) => (
                                            <ActivityEntry key={entry.id} entry={entry} />
                                        ))}
                                    </div>
                                    <div style={{ marginTop: 16, textAlign: "center" }}>
                                        <button onClick={() => showToast("Exporting activity log…", "info")} style={{ fontSize: 13, padding: "8px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                                            Export full log (CSV)
                                        </button>
                                    </div>
                                </SectionCard>
                            )}

                            {/* ─────────────────────── DANGER ZONE ─────────────────────── */}
                            {activeSection === "danger" && (
                                <>
                                    <SectionCard title="Danger Zone" subtitle="Irreversible and destructive actions — proceed with caution" danger>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                            {[
                                                {
                                                    label: "Export all account data",
                                                    desc: "Download a full export of all your account data including products, certifications, invoices, and settings as a JSON archive.",
                                                    btnLabel: "Export data",
                                                    action: () => showToast("Preparing data export… you will receive an email when ready.", "info"),
                                                    danger: false,
                                                },
                                                {
                                                    label: "Reset dashboard settings",
                                                    desc: "Restore all notification preferences, appearance, and security settings to their factory defaults. Your content will not be affected.",
                                                    btnLabel: "Reset settings",
                                                    action: () => { setNotifs(MOCK_NOTIFICATIONS); setSecurity(s => ({ ...s, sessionTimeout: "8h", loginNotifications: true })); showToast("Dashboard settings reset to defaults", "warning"); },
                                                    danger: true,
                                                },
                                                {
                                                    label: "Revoke all API keys",
                                                    desc: "Immediately invalidate all active API keys. Any integrations using these keys will stop working until new keys are generated.",
                                                    btnLabel: "Revoke all keys",
                                                    action: () => { setApiKeys([]); showToast("All API keys revoked", "warning"); },
                                                    danger: true,
                                                },
                                                {
                                                    label: "Sign out of all devices",
                                                    desc: "Immediately terminate all active sessions except your current one. All other devices will be signed out.",
                                                    btnLabel: "Sign out all devices",
                                                    action: () => { setSecurity(s => ({ ...s, activeSessions: s.activeSessions.filter(se => se.isCurrent) })); showToast("All other sessions terminated", "warning"); },
                                                    danger: true,
                                                },
                                                {
                                                    label: "Deactivate account",
                                                    desc: "Temporarily deactivate your admin account. Your content and data will be preserved but you will not be able to log in until reactivated by another administrator.",
                                                    btnLabel: "Deactivate account",
                                                    action: () => showToast("Account deactivation request sent to system administrator", "warning"),
                                                    danger: true,
                                                },
                                            ].map((item, i, arr) => (
                                                <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "16px 0", borderBottom: i < arr.length - 1 ? "1px solid rgba(224,82,82,0.12)" : "none" }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 14, fontWeight: 500, color: item.danger ? "#e05252" : "var(--text)", marginBottom: 3 }}>{item.label}</div>
                                                        <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.6 }}>{item.desc}</div>
                                                    </div>
                                                    {item.danger ? (
                                                        <DangerBtn onClick={() => setConfirm({ open: true, title: item.label + "?", message: item.desc + " This action cannot be undone.", label: item.btnLabel, action: item.action, danger: true })} label={item.btnLabel} />
                                                    ) : (
                                                        <button onClick={item.action} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap" }}>{item.btnLabel}</button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </SectionCard>
                                </>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}